/** @jsx jsx */
import { jsx } from '@emotion/core';
import { navigate } from '@reach/router';
import { withTheme } from 'emotion-theming';
// eslint-disable-next-line no-restricted-imports
import { Link as GatsbyLink } from 'gatsby';
import {
  getButtonStyles,
  getLinkStyles,
  Icon,
  IconType,
  Link as LucidLink,
  LinkVariant
} from 'lucid-components-react';
import { ThemeProps } from 'lucid-components-react/dist/theme/theme';
import { FC, HTMLProps, MouseEvent } from 'react';
import {
  MarketingPageElementLocation,
  MarketingPageElementType
} from '../../../src/types/beaconEventTypes';
import { sendBeaconEvent } from '../../utils/events/send-beacon-event';
import useSiteMetadata from '../../hooks/use-site-metadata';
import usePathPrefix from '../../hooks/use-path-prefix';

interface Props {
  to: string;
  active?: boolean;
  activeClassName?: string;
  excludeFromGatsby?: boolean;
  partiallyActive?: boolean;
  checkHistory?: boolean;
  hasArrow?: boolean;
  newTab?: boolean;
  externalAlwaysNewTab?: boolean;
  theme: ThemeProps['theme'];
  beaconEvent?: {
    name: string;
    elementLocation: MarketingPageElementLocation;
    elementDetail: string;
    elementType: MarketingPageElementType;
  } | null;
  blacklist?: string[];
}

// Lucid Link
interface LucidLinkProps {
  disabled?: boolean;
  primary?: boolean;
  secondary?: boolean;
  inverse?: boolean;
  asButton?: boolean;
  block?: boolean;
  underline?: 'none' | 'hover' | 'always';
  buttonSize?: 'small' | 'regular' | 'large';
  ref?: any;
}

export type LinkInterface = Props &
  HTMLProps<HTMLAnchorElement> &
  LucidLinkProps;

// Since DOM elements <a> cannot receive activeClassName
// and partiallyActive, destructure the prop here and
// pass it only to GatsbyLink
const LinkBase: FC<LinkInterface> = ({
  children,
  to,
  asButton,
  buttonSize = 'regular',
  block,
  excludeFromGatsby,
  primary,
  inverse,
  secondary,
  className = '',
  activeClassName,
  partiallyActive,
  checkHistory,
  hasArrow = primary,
  underline = 'hover',
  theme,
  ref,
  onClick,
  beaconEvent,
  defaultValue,
  newTab,
  blacklist,
  externalAlwaysNewTab,
  ...other
}) => {
  const { siteDomain } = useSiteMetadata();
  const pathPrefix = usePathPrefix();

  if (!to) {
    // console.error('Invalid link. Missing to field', children);
    return null;
  }
  /*
    The following regex does the following:
      1. Matches if the url starts with `/pages/`
      2. Matches if the url starts with `http://www.lucidchart.com/pages` or `https://www.lucidchart.com/pages/` or `www.lucidchart.com/pages/` or `http://lucidchart.com/pages/` or `https://lucidchart.com/pages/`
      3. Matches if the url starts with `/` but doesn't start with `/blog` and `/documents` and `/users` and `/es/users/`
  (?:users?|blog|documents)
  */
  const blackListRegex = blacklist
    ? `(?!(?:../|..-../)?(?:${blacklist.join('|')}))`
    : '';
  const pathPrefixRegex = pathPrefix ? `${pathPrefix}/|` : '';
  const hybridRegex = new RegExp(
    `^(?:(?://|https?://)?(?:www.)?${siteDomain}(?:${pathPrefixRegex}/$|$)/?|${pathPrefixRegex}/(?!/)${blackListRegex})(.*)`
  );
  const internal = hybridRegex.exec(to);
  const underlineAlways = underline === 'always';
  underline = asButton ? 'none' : underline;
  const linkUnderlineHover =
    underline === 'hover' || underline === 'always'
      ? 'link--hover-underline'
      : '';
  const style = underlineAlways
    ? {
        textDecoration: 'underline'
      }
    : {};

  let linkVariant = LinkVariant.Default;
  if (primary) {
    linkVariant = LinkVariant.Primary;
    underline = 'hover';
  } else if (secondary) {
    linkVariant = LinkVariant.Secondary;
    underline = 'hover';
  }

  let styles = {};

  if (asButton) {
    hasArrow = false;
    styles = {
      ...getButtonStyles({
        theme,
        variant: linkVariant,
        block,
        size: buttonSize
      }),
      ...style
    };
  } else {
    styles = {
      ...getLinkStyles({
        theme,
        block,
        variant: linkVariant,
        underline,
        inverse
      }),
      ...style
    };
  }

  // Currently only used on case-study template breadcrumb links
  // to utillize the 'back' functionality, which will keep previous query
  //  params, rather than navigate back to the previous page loaded new
  // without query params.
  const beforeNavigation = (e: MouseEvent<HTMLAnchorElement>) => {
    if (checkHistory) {
      const prevLocation = sessionStorage.getItem('prevLocation');
      const baseTo = to.split('?')[0];
      if (baseTo === prevLocation) {
        e.preventDefault();
        window.history.back();
      }
    }
  };

  const isAnchorPath = (p: string) => p.startsWith('#');

  const onClickHandler = (e: MouseEvent<HTMLAnchorElement>) => {
    if (beaconEvent) {
      sendBeaconEvent(beaconEvent);
    }
    beforeNavigation(e);
    onClick?.(e);
    if (isAnchorPath(to)) {
      e.preventDefault();
      navigate(to);
    }
  };

  const newTabProps =
    newTab || externalAlwaysNewTab
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};

  // Use Gatsby Link for internal links that open in the same tab, and <a> for others
  if (internal && !newTab && !excludeFromGatsby) {
    return (
      <GatsbyLink
        {...other}
        activeClassName={activeClassName}
        className={`link ${className} ${linkUnderlineHover}`}
        partiallyActive={partiallyActive}
        data-is-gatsby
        onClick={onClickHandler}
        css={styles}
        to={`/${internal[1]}`}
      >
        {children}
        {hasArrow && (
          <span className="link--has-arrow">
            <Icon type={IconType.ArrowRight} />
          </span>
        )}
      </GatsbyLink>
    );
  }

  // used to find product links with our plugin. Once we address the source
  // of all product links still going to www we will no longer need this.
  // uncomment this and line 235 for testing with plugin

  // const goesToProduct = to.includes('/users/') || to.includes('/documents/');
  return (
    <LucidLink
      {...other}
      {...newTabProps}
      asButton={asButton}
      block={block}
      className={`link ${className}`}
      href={to}
      primary={primary}
      secondary={secondary}
      inverse={inverse}
      underline={underline}
      onClick={onClickHandler}
      // data-is-product-link={goesToProduct}
    >
      {children}
      {hasArrow && !asButton && (
        <span className="link--has-arrow">
          <Icon type={IconType.ArrowRight} />
        </span>
      )}
    </LucidLink>
  );
};

const Link = withTheme(LinkBase);
export default Link;
